import { formatSignedWeight } from 'shared/utilities/format';

function polar(cx, cy, radius, angle) {
  const radians = ((angle - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(radians),
    y: cy + radius * Math.sin(radians),
  };
}

export function SignedRadialChart({ weights = [], locale = 'en' }) {
  const size = 360;
  const cx = size / 2;
  const cy = size / 2;
  const baseline = 92;
  const maxOffset = 68;
  const items = weights.map((item) => {
    const parsed = item.weight == null || item.weight === '' ? null : Number(item.weight);
    return {
      ...item,
      value: Number.isFinite(parsed) ? parsed : null,
    };
  });

  const summary = items
    .map((item) => `${item.name_en || item.name}: ${item.value == null ? '—' : formatSignedWeight(item.value)}`)
    .join('; ');

  return (
    <figure className="signed-chart">
      <svg viewBox={`0 0 ${size} ${size}`} role="img" aria-label={summary}>
        <circle cx={cx} cy={cy} r={baseline} className="signed-chart-baseline" fill="none" />
        <circle cx={cx} cy={cy} r={8} className="signed-chart-origin" />
        {items.map((item, index) => {
          const angle = (360 / Math.max(items.length, 1)) * index;
          const numeric = item.value == null ? 0 : item.value;
          const magnitude = Math.min(100, Math.abs(numeric)) / 100;
          const outer = numeric >= 0 ? baseline + magnitude * maxOffset : baseline;
          const inner = numeric < 0 ? baseline - magnitude * maxOffset : baseline;
          const start = polar(cx, cy, inner, angle);
          const end = polar(cx, cy, outer === inner ? baseline + 6 : outer, angle);
          const labelPoint = polar(cx, cy, 168, angle);
          const tone = numeric > 0 ? 'positive' : numeric < 0 ? 'negative' : 'zero';
          return (
            <g key={item.professional_pattern_id || item.id || index}>
              <line
                x1={start.x}
                y1={start.y}
                x2={end.x}
                y2={end.y}
                data-testid={`signed-bar-${tone}`}
                className={`signed-chart-bar signed-chart-${tone}`}
                stroke={item.color || 'currentColor'}
                strokeWidth={numeric === 0 ? 5 : 10}
                strokeLinecap="round"
              />
              <text x={labelPoint.x} y={labelPoint.y} textAnchor="middle" className="signed-chart-label">
                {item.value == null ? '—' : formatSignedWeight(item.value)}
              </text>
            </g>
          );
        })}
      </svg>
      <figcaption className="sr-only">{summary}</figcaption>
      <ul className="chart-legend">
        {items.map((item) => (
          <li key={item.professional_pattern_id || item.id}>
            <span className="swatch" style={{ background: item.color || '#94a3b8' }} />
            <span>{locale === 'ar' ? item.name_ar : item.name_en || item.name}</span>
            <strong>{item.value == null ? '—' : formatSignedWeight(item.value)}</strong>
          </li>
        ))}
      </ul>
      <table className="data-table">
        <caption className="sr-only">{summary}</caption>
        <thead>
          <tr>
            <th>{locale === 'ar' ? 'النمط' : 'Pattern'}</th>
            <th>{locale === 'ar' ? 'الوزن' : 'Weight'}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={`table-${item.professional_pattern_id || item.id}`}>
              <td>{locale === 'ar' ? item.name_ar : item.name_en || item.name}</td>
              <td>{item.value == null ? '—' : formatSignedWeight(item.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
